/**
 * Day0TutorMemoryModal — captures the "Tell it you're premed — once" hook.
 *
 * Fires the FIRST time a user lands on /app (gated by localStorage flag
 * `istudypro_day0_memory_v1`). Three quick fields → POST /api/tutor-memory/manual
 * three times so each becomes a distinct, retrievable memory chunk that the
 * AI tutor reads on every future chat.
 *
 * Skippable, but heavily nudged: the whole landing-page promise
 * ("A tutor that remembers you") collapses if we never capture this data.
 *
 * Design: F500 dark-glass over Klein-blue accent. Mobile-first.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Sparkles, ArrowRight, X, Calendar, GraduationCap, Compass } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

const FLAG_KEY = 'istudypro_day0_memory_v1';

const FIELDS = [
  {
    id: 'major',
    icon: GraduationCap,
    label: "What are you studying?",
    placeholder: "e.g. Premed, Computer Science, Economics, WASSCE prep…",
    kind: 'goal',
    prefix: 'Major / Programme:',
    required: true,
  },
  {
    id: 'goal',
    icon: Compass,
    label: 'What\'s your end-game?',
    placeholder: 'e.g. MCAT in May, first-class honours, GPA above 3.7…',
    kind: 'goal',
    prefix: 'Long-term goal:',
    required: false,
  },
  {
    id: 'exam',
    icon: Calendar,
    label: "Any big exam coming up?",
    placeholder: 'e.g. Anatomy midterm Mar 15, Finals end of May…',
    kind: 'goal',
    prefix: 'Upcoming exam:',
    required: false,
  },
];

export default function Day0TutorMemoryModal() {
  const [show, setShow] = useState(false);
  const [values, setValues] = useState({ major: '', goal: '', exam: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (localStorage.getItem(FLAG_KEY)) return;
        // If they already have memory captured (e.g. via tutor chat), skip.
        const res = await api.get(apiUrl('/tutor-memory'));
        if (cancel) return;
        if ((res.data?.count || 0) > 0) {
          localStorage.setItem(FLAG_KEY, '1');
          return;
        }
        // Small delay so the user gets to see the dashboard first.
        setTimeout(() => !cancel && setShow(true), 900);
      } catch {
        // If the API fails, don't show the modal — never block dashboard.
      }
    })();
    return () => { cancel = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(FLAG_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
  };

  const save = async () => {
    const major = values.major.trim();
    if (!major) {
      toast.error('Tell us what you\'re studying — even a guess is enough.');
      return;
    }
    setSaving(true);
    try {
      const payload = FIELDS
        .map((f) => ({ kind: f.kind, content: values[f.id]?.trim() ? `${f.prefix} ${values[f.id].trim()}` : null }))
        .filter((p) => p.content);
      // Sequential to keep ordering deterministic in the memory list.
      for (const p of payload) {
        await api.post(apiUrl('/tutor-memory/manual'), p);
      }
      toast.success('Locked in. Your tutor will remember this from now on. 🔒');
      try { localStorage.setItem(FLAG_KEY, '1'); } catch { /* ignore */ }
      setShow(false);
    } catch (err) {
      console.error('Day0 memory save failed:', err);
      toast.error('Couldn\'t save — try again, or skip and add it later in Settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(12,18,34,0.78)', backdropFilter: 'blur(6px)' }}
          data-testid="day0-memory-modal"
        >
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            className="w-full max-w-lg bg-[var(--isp-page)] border border-[var(--isp-glass-border)] shadow-2xl"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--isp-glass-border)]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-[#002FA7]/12">
                  <Brain className="w-4 h-4 text-[#002FA7]" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--isp-muted)]">
                  Tutor Memory · setup
                </p>
              </div>
              <button
                onClick={dismiss}
                className="p-1.5 text-[var(--isp-muted)] hover:text-[var(--isp-heading)]"
                data-testid="day0-memory-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 sm:px-6 py-6">
              <h2
                className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] tracking-tight mb-2"
                style={{ fontFamily: 'var(--sf-display)' }}
              >
                Tell us once. We'll remember forever.
              </h2>
              <p className="text-sm text-[var(--isp-muted)] mb-6 leading-relaxed">
                The AI tutor uses this on every future chat — so you never have to repeat "I'm premed, prepping for the MCAT" again. Three quick fields. Skippable any time.
              </p>

              <div className="space-y-4" data-testid="day0-memory-form">
                {FIELDS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.id}>
                      <label className="flex items-center gap-2 text-xs font-semibold text-[var(--isp-heading)] mb-1.5">
                        <Icon className="w-3.5 h-3.5 text-[#002FA7]" />
                        {f.label}
                        {f.required && <span className="text-[#002FA7]">*</span>}
                      </label>
                      <input
                        type="text"
                        value={values[f.id]}
                        onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full px-3 py-2.5 bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] focus:border-[#002FA7] outline-none text-sm text-[var(--isp-heading)] placeholder:text-[var(--isp-muted)] transition-colors"
                        data-testid={`day0-memory-${f.id}-input`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-t border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)]/40">
              <button
                onClick={dismiss}
                className="text-xs text-[var(--isp-muted)] hover:text-[var(--isp-heading)] transition-colors"
                data-testid="day0-memory-skip"
              >
                Skip for now
              </button>
              <button
                onClick={save}
                disabled={saving || !values.major.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] disabled:bg-[var(--isp-muted)] disabled:cursor-not-allowed text-white font-semibold text-sm tracking-tight transition-colors"
                data-testid="day0-memory-save"
              >
                {saving ? 'Saving…' : (<><Sparkles className="w-3.5 h-3.5" /> Lock it in <ArrowRight className="w-3.5 h-3.5" /></>)}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
